import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Undo2 } from "lucide-react";
import {
  AssetReturnReasonView,
  CreateAssetReturnReasonInput,
  UpdateAssetReturnReasonInput,
  getReturnReasons,
  createReturnReason,
  updateReturnReason,
  archiveReturnReason,
} from "../../../../api/General/assets";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import AlertDialog from "@/components/ui/AlertDialog";
import { Spinner } from "@/components/ui/Spinner";
import { useToast, ToastRenderer } from "@/hooks/useToast";

export default function ReturnReasonsTab() {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [reasons, setReasons] = useState<AssetReturnReasonView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Modal state
  const [formOpen, setFormOpen] = useState(false);
  const [editingReason, setEditingReason] = useState<AssetReturnReasonView | null>(null);
  const [saving, setSaving] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<AssetReturnReasonView | null>(null);
  const [archiving, setArchiving] = useState(false);

  // Form fields
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadReasons = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await getReturnReasons();
      if (result.errors.length === 0) {
        setReasons(result.payload);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReasons();
  }, [loadReasons]);

  const openAdd = () => {
    setEditingReason(null);
    setReason("");
    setErrors({});
    setFormOpen(true);
  };

  const openEdit = (r: AssetReturnReasonView) => {
    setEditingReason(r);
    setReason(r.reason);
    setErrors({});
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingReason(null);
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!reason.trim()) errs.reason = t("assetReturnReasons.reasonRequired");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      if (editingReason) {
        const input: UpdateAssetReturnReasonInput = {
          id: editingReason.id,
          reason: reason.trim(),
        };
        const result = await updateReturnReason(input);
        if (result.errors.length === 0) {
          showToast(t("assetReturnReasons.reasonUpdated"), "success");
          closeForm();
          loadReasons();
        } else {
          showToast(result.errors[0]?.description || t("Something went wrong"), "error");
        }
      } else {
        const input: CreateAssetReturnReasonInput = {
          reason: reason.trim(),
        };
        const result = await createReturnReason(input);
        if (result.errors.length === 0) {
          showToast(t("assetReturnReasons.reasonCreated"), "success");
          closeForm();
          loadReasons();
        } else {
          showToast(result.errors[0]?.description || t("Something went wrong"), "error");
        }
      }
    } catch {
      showToast(t("Something went wrong"), "error");
    } finally {
      setSaving(false);
    }
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      const result = await archiveReturnReason(archiveTarget.id);
      if (result.errors.length === 0) {
        showToast(t("assetReturnReasons.reasonArchived"), "success");
        loadReasons();
      } else {
        showToast(result.errors[0]?.description || t("Something went wrong"), "error");
      }
    } catch {
      showToast(t("Something went wrong"), "error");
    } finally {
      setArchiving(false);
      setArchiveTarget(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">{t("assetReturnReasons.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("assetReturnReasons.subtitle")}</p>
        </div>
        <Button
          variant="outline"
          onClick={openAdd}
          data-testid="add-return-reason-btn"
        >
          <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t("assetReturnReasons.addReason")}
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12" data-testid="return-reasons-error">
          <p className="text-sm text-destructive">{t("assetReturnReasons.loadError")}</p>
        </div>
      ) : reasons.length === 0 ? (
        <div className="text-center py-12" data-testid="return-reasons-empty">
          <Undo2 className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-2 text-sm font-medium text-foreground">{t("assetReturnReasons.noReasons")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("assetReturnReasons.noReasonsDescription")}</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetReturnReasons.reason")}
                </th>
                <th className="px-4 py-3 text-end text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetReturnReasons.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {reasons.map((r) => (
                <tr key={r.id} className="hover:bg-muted/30 transition-colors" data-testid={`return-reason-row-${r.id}`}>
                  <td className="px-4 py-3 text-sm font-medium text-foreground">{r.reason}</td>
                  <td className="px-4 py-3 text-sm text-end">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => openEdit(r)}
                        data-testid={`edit-return-reason-${r.id}`}
                      >
                        <svg className="w-3.5 h-3.5 me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        {t("common.edit")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => setArchiveTarget(r)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`archive-return-reason-${r.id}`}
                      >
                        <svg className="w-3.5 h-3.5 me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        {t("assets.archive")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit Modal */}
      <Dialog open={formOpen} onOpenChange={(o) => !o && closeForm()}>
        <DialogContent size="default">
          <DialogHeader>
            <DialogTitle>
              {editingReason ? t("assetReturnReasons.editReason") : t("assetReturnReasons.addReason")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <DialogBody className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assetReturnReasons.reason")} *
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${errors.reason ? "border-destructive" : "border-border"}`}
                  data-testid="return-reason-input"
                  autoFocus
                />
                {errors.reason && <p className="text-destructive text-xs mt-1">{errors.reason}</p>}
              </div>
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeForm}
                data-testid="return-reason-form-cancel"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                loading={saving}
                data-testid="return-reason-form-save"
              >
                {saving ? t("common.saving") : editingReason ? t("common.save") : t("assetReturnReasons.addReason")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation */}
      <AlertDialog
        open={!!archiveTarget}
        onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}
        title={t("assets.archive")}
        description={t("assetReturnReasons.confirmArchive")}
        variant="destructive"
        confirmLabel={t("assets.archive")}
        cancelLabel={t("common.cancel")}
        isLoading={archiving}
        onConfirm={confirmArchive}
      />

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
}
